import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Info } from "lucide-react"
import type { ReaderHint } from "../../../../../api/types"
import { readerHintLines } from "./readerHintNote"

/** How many hints a card lists before folding the rest into one count line
 *  (worst kinds first): a blanket statement over a large table can carry
 *  hundreds. */
export const MAX_READER_HINT_LINES = 3

/**
 * The inquiry template reader's hints for `articles`, in the quiet note
 * register of the article row (`.sv2-artnote`, an `Info` icon): the sheet
 * row in bold, then this app's own sentence. Advisory only, never a gate,
 * never the cell text. Renders nothing when no known hint concerns these
 * articles; which articles a card passes is `cardHintArticles`' rule.
 *
 * With `onJumpToRow`, each line is a button that lands its sheet row in the
 * workbook pane, so the reviewer can read the row the hint is about.
 */
export function ReaderHintNotes({
  byArticle,
  articles,
  wrapClassName,
  onJumpToRow,
}: {
  /** One Verordnung's hints keyed by article number. */
  byArticle?: ReadonlyMap<string, readonly ReaderHint[]>
  articles: Iterable<string>
  /** Wraps the notes in one element of this class (the remainder card's
   *  slot), so an empty result leaves no empty slot behind. */
  wrapClassName?: string
  /** Lands a sheet row (1-based) in the workbook pane. Omitted: the lines
   *  stay plain notes. */
  onJumpToRow?: (sheet: string, row: number) => void
}) {
  const { translate } = useTranslation()
  const lines = readerHintLines(byArticle, articles)
  if (lines.length === 0) return null
  const shown = lines.slice(0, MAX_READER_HINT_LINES)
  const extra = lines.length - shown.length
  const notes = (
    <>
      {shown.map((line) => {
        const body = (
          <>
            <Info className="sv2-ic" aria-hidden="true" />
            <span>
              <b>
                {translate("MC_DECL_WB_AT", {
                  sheet: line.sheet,
                  ref: translate("MC_DECL_V2_READER_HINT_ROW", {
                    row: line.row,
                  }),
                })}
              </b>{" "}
              {translate(line.key)}
            </span>
          </>
        )
        const key = `${line.kind}:${line.sheet}:${line.row}`
        return onJumpToRow ? (
          <button
            type="button"
            className="sv2-artnote sv2-artnote-link"
            key={key}
            data-testid="reader-hint"
            data-kind={line.kind}
            title={translate("MC_DECL_V2_READER_HINT_JUMP")}
            onClick={() => onJumpToRow(line.sheet, line.row)}
          >
            {body}
          </button>
        ) : (
          <div
            className="sv2-artnote"
            key={key}
            data-testid="reader-hint"
            data-kind={line.kind}
          >
            {body}
          </div>
        )
      })}
      {extra > 0 && (
        <div className="sv2-artnote" data-testid="reader-hint-more">
          <Info className="sv2-ic" aria-hidden="true" />
          {translate("MC_DECL_V2_READER_HINT_MORE", { k: extra })}
        </div>
      )}
    </>
  )
  return wrapClassName ? <div className={wrapClassName}>{notes}</div> : notes
}
